import * as React from "react";
import axios from "axios";
import { Box, Button, Typography, FormControl, InputLabel, Select, MenuItem, TextField, Grid, ToggleButtonGroup, ToggleButton } from "@mui/material";
import LandlordsTable from "components/Tables/LandlordsTable";
import TopSnack from "components/TopSnack";
import { useLandlords } from "services/hooks/rentals";
import { baseURL } from "services/API";
import FormDialog from "components/modal"; 
import AddLandlordForm from "components/Forms/LandlordForm"; 
import { useState, useEffect } from "react";
import LandlordsPopover from "components/PopOvers/LandlordsPopOver";
import EditLandlordForm from "components/LandlordModals/EditLandlord";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setLandlord } from "state";
import * as yup from "yup";
import { useEinvoicing } from "services/hooks/einvoicing";
import EfrisOnboardingDialog from "components/LandlordModals/EfrisOnboardingDialog";

const PAY_WEEK_DAYS = ["MONDAY", "TUESDAY", "WEDNESDAY", "THURSDAY", "FRIDAY", "SATURDAY", "SUNDAY"];

/** Parse contact to +256 MSISDN; accepts +256…, 256…, or 07… */
const parseLandlordContact = (raw) => {
  const trimmed = (raw || "").trim().replace(/\s/g, "");
  if (!trimmed) return null;
  if (/^\+256[0-9]{9}$/.test(trimmed)) {
    return trimmed;
  }
  if (/^0[0-9]{9}$/.test(trimmed)) {
    return `+256${trimmed.slice(1)}`;
  }
  if (/^256[0-9]{9}$/.test(trimmed)) {
    return `+${trimmed}`;
  }
  return null;
};

const emptyNewLandlord = {
  contact: "",
  first_name: "",
  last_name: "",
  email: "",
  business_name: "",
  pay_percentage: undefined,
  payout_frequency: "MONTHLY",
  pay_day: "",
  pay_week_day: "",
  landlord_payment_mode: "MOBILE_MONEY",
  landlord_payment_account: "",
  landlord_mobile_registered_name: "",
  landlord_bank_name: "",
  landlord_bank_account_number: "",
  landlord_bank_account_name: "",
  landlord_bank_branch: "",
  landlord_bank_sort_code: "",
};

const buildCreateLandlordPayload = (form) => {
  const msisdn = parseLandlordContact(form.contact);
  const payload = {
    username: msisdn,
    first_name: form.first_name?.trim(),
    last_name: form.last_name?.trim(),
    email: form.email?.trim() || "",
    phone_number: msisdn,
    business_name: form.business_name?.trim(),
    payout_frequency: form.payout_frequency || "MONTHLY",
    pay_day: form.payout_frequency === "MONTHLY" ? String(form.pay_day ?? "").trim() : "",
    pay_week_day: form.payout_frequency === "WEEKLY" ? form.pay_week_day : "",
    pay_percentage:
      form.pay_percentage !== undefined && form.pay_percentage !== null && form.pay_percentage !== ""
        ? String(form.pay_percentage)
        : "",
    landlord_payment_mode: form.landlord_payment_mode || "MOBILE_MONEY",
  };

  if (payload.landlord_payment_mode === "MOBILE_MONEY") {
    const account = form.landlord_payment_account?.trim() || msisdn;
    if (account) payload.landlord_payment_account = account;
    payload.landlord_mobile_registered_name = form.landlord_mobile_registered_name?.trim();
  }

  if (payload.landlord_payment_mode === "BANK") {
    payload.landlord_bank_name = form.landlord_bank_name?.trim();
    payload.landlord_bank_account_number = form.landlord_bank_account_number?.trim();
    payload.landlord_bank_account_name = form.landlord_bank_account_name?.trim();
    payload.landlord_bank_branch = form.landlord_bank_branch?.trim() || "";
    if (form.landlord_bank_sort_code?.trim()) {
      payload.landlord_bank_sort_code = form.landlord_bank_sort_code.trim();
    }
  }

  return payload;
};

const Landlords = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openDialog, setOpenDialog] = React.useState(false);
  const [filterType, setFilterType] = React.useState("");
  const [open, setOpen] = React.useState(false);
  const [modal, setModal] = React.useState("");
  const [selectedLandlord, setSelectedLandlord] = React.useState({});
  const [successMessage, setSuccessMessage] = useState("")
  const [errorMessage,setErrorMessage] = useState("")
  const openPopper = Boolean(anchorEl);
  const popperId = openPopper ? "simple-popover" : undefined;
  const [activeRental, setActiveRental] = React.useState("");
  const [openSnack, setOpenSnack] = React.useState({open: false,message: "",severity: "success"});
  const handleCloseSnack = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setOpenSnack({ ...openSnack, open: false });
  };
  const [message, setMessage] = React.useState("");

  const [newLandlord, setNewLandlord] = React.useState({ ...emptyNewLandlord });

  const [validationErrors, setValidationErrors] = React.useState({});
  const [isSubmittingData, setIsSubmittingData] = React.useState(false);
  const [inactivateDate, setInactivateDate] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("all"); // "all" | "active" | "inactivated" | "anonymous"

  const { landlords, isLoadingLandlords, error, setNewItemData } = useLandlords({
    inactivation: statusFilter === "anonymous" ? "all" : statusFilter,
    anonymous: statusFilter === "anonymous",
  });
  const { isSubmitting: isTogglingEinvoicing, setEinvoicingEnabled } = useEinvoicing();

  useEffect(() => {
    if (modal === "inactivate") {
      setInactivateDate(new Date().toISOString().slice(0, 10)); // today YYYY-MM-DD
    }
  }, [modal]);

  const handleMenuClick = (event, landlord) => {
    setAnchorEl(event.currentTarget);
    setSelectedLandlord(landlord);
  };

   const handleClose = () => {
    setOpen(false);
    setModal("");
    setValidationErrors({});
  };

  const handleClickPopOver = (event, row) => {
    setAnchorEl(event.currentTarget);
    setActiveRental(row);
  };

  const handleClosePopOver = () => {
    setAnchorEl(null);
  }; 
  
  const handleClickOpen = () => {
    setOpen(true);
    handleClosePopOver();
  };

  const handleModalView = (modalView) => {
    setModal(modalView);
  };

  const handleViewLandlord = () => {
    dispatch(setLandlord(selectedLandlord));
    navigate(`/landlordDetails`, {state : selectedLandlord});
  };

  const handleSendMessage = async () => {
    if (selectedLandlord && message) {
      // await onSendMessage(selectedLandlord, message);
      handleDialogClose();
      setMessage("");
    }
  };

  const handleDelete = async () => {
    setIsSubmittingData(true);
    try {
        const response = await axios.delete(`${baseURL}/landlords/admin/landlords/details/${selectedLandlord.id}`);
        
        // Check if the response indicates a successful deletion
        if (response.status === 200) {
            setOpenSnack({
                open: true,
                message: "Landlord deleted successfully",
                severity: "success",
            });
        } else {
            setOpenSnack({
                open: true,
                message: "Failed to delete landlord",
                severity: "error",
            });
        }
    } catch (error) {
        // Handle errors from the delete request
        console.error(error);
        setOpenSnack({
            open: true,
            message: error.response?.data?.message || "Failed to delete landlord",
            severity: "error",
        });
    } finally {
        setIsSubmittingData(false);
    }
    handleClose(false);
    setNewItemData(true);
    setSelectedLandlord({});
  };

  const handleInactivate = async () => {
    if (!inactivateDate) return;
    setIsSubmittingData(true);
    try {
      const date_inactivated = `${inactivateDate}T00:00:00.000Z`;
      await axios.patch(`${baseURL}/landlords/admin/landlords/details/${selectedLandlord.id}`, {
        inactivate: true,
        date_inactivated,
      });
      setOpenSnack({ open: true, message: "Landlord inactivated successfully", severity: "success" });
      setNewItemData(true);
      handleClose();
      setSelectedLandlord({});
    } catch (error) {
      console.error(error);
      setOpenSnack({
        open: true,
        message: error.response?.data?.message || "Failed to inactivate landlord",
        severity: "error",
      });
    } finally {
      setIsSubmittingData(false);
    }
  };

  const handleReactivate = async () => {
    setIsSubmittingData(true);
    try {
      await axios.patch(`${baseURL}/landlords/admin/landlords/details/${selectedLandlord.id}`, { reactivate: true });
      setOpenSnack({ open: true, message: "Landlord reactivated successfully", severity: "success" });
      setNewItemData(true);
      handleClose();
      setSelectedLandlord({});
    } catch (error) {
      console.error(error);
      setOpenSnack({
        open: true,
        message: error.response?.data?.message || "Failed to reactivate landlord",
        severity: "error",
      });
    } finally {
      setIsSubmittingData(false);
    }
  };

  const [efrisLandlord, setEfrisLandlord] = React.useState(null);

  const handleOpenEfrisOnboarding = () => {
    if (!selectedLandlord?.id) return;
    setEfrisLandlord(selectedLandlord);
    handleClosePopOver();
  };

  const handleToggleEinvoicing = async (enabled) => {
    if (!selectedLandlord?.id) return;
    handleClosePopOver();
    const result = await setEinvoicingEnabled(selectedLandlord.id, enabled);
    if (result.success) {
      setOpenSnack({
        open: true,
        message: enabled
          ? "E-invoicing enabled for this landlord."
          : "E-invoicing disabled for this landlord.",
        severity: "success",
      });
      setNewItemData(true);
      setSelectedLandlord({});
    } else {
      setOpenSnack({
        open: true,
        message: result.message || "Failed to update e-invoicing.",
        severity: "error",
      });
    }
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
    setNewLandlord({ ...emptyNewLandlord });
    setValidationErrors({});
  };

  const normalFieldsSchema = yup.object().shape({
    contact: yup
      .string()
      .required("Contact number is required")
      .test("format", "Use format +256712345678 (or 0712345678)", (value) => !!parseLandlordContact(value))
      .test(
        "check-contact-available",
        "This number is already registered. Use another contact.",
        async (value) => {
          const msisdn = parseLandlordContact(value);
          if (!msisdn) return false;
          try {
            const formData = new FormData();
            formData.append("phone_number", msisdn);
            await axios.post(`${baseURL}/accounts/users/details`, formData);
            return false;
          } catch {
            return true;
          }
        }
      ),
    first_name: yup.string().required("First name is required"),
    last_name: yup.string().required("Last name is required"),
    email: yup
      .string()
      .transform((value) => (value === "" || value == null ? null : value))
      .nullable()
      .email("Email is invalid"),
    business_name: yup.string().required("Business name is required"),
    pay_percentage: yup
      .number()
      .min(1, "Payment percentage must be between 1 and 100%")
      .max(100, "Payment percentage must be between 1 and 100%")
      .required("Pay percentage is required"),
    payout_frequency: yup.string().oneOf(["MONTHLY", "WEEKLY", "DAILY"]).required("Payout frequency is required"),
    pay_day: yup.string().when("payout_frequency", {
      is: "MONTHLY",
      then: (schema) => schema.required("Pay day is required").max(5, "Pay day must not exceed 5 characters"),
      otherwise: (schema) => schema.notRequired(),
    }),
    pay_week_day: yup.string().when("payout_frequency", {
      is: "WEEKLY",
      then: (schema) => schema.required("Pay week day is required").oneOf(PAY_WEEK_DAYS),
      otherwise: (schema) => schema.notRequired(),
    }),
    landlord_payment_mode: yup
      .string()
      .oneOf(["MOBILE_MONEY", "BANK", "CASH", "OTHER"])
      .required("Payment mode is required"),
    landlord_mobile_registered_name: yup.string().when("landlord_payment_mode", {
      is: "MOBILE_MONEY",
      then: (schema) => schema.required("Name on mobile money wallet is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    landlord_bank_name: yup.string().when("landlord_payment_mode", {
      is: "BANK",
      then: (schema) => schema.required("Bank name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    landlord_bank_account_number: yup.string().when("landlord_payment_mode", {
      is: "BANK",
      then: (schema) => schema.required("Account number is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    landlord_bank_account_name: yup.string().when("landlord_payment_mode", {
      is: "BANK",
      then: (schema) => schema.required("Account name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    landlord_bank_branch: yup.string().when("landlord_payment_mode", {
      is: "BANK",
      then: (schema) => schema.required("Branch is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const landlordSchema = normalFieldsSchema;

  const editLandlordSchema = yup.object().shape({
    phone_number: yup.string().required("Phone Numberis required").matches(/^(0\d{9})$/, "Phone number must be in the format 076337019"),
    first_name: yup.string().required("First name is required"),
    last_name: yup.string().required("Last name is required"),
    email: yup.string().email("Email is invalid"),
    business_name: yup.string().required("Business Name is required"),
    pay_percentage: yup.number().min(1,"Payment Percentage must be between 0 and 100%").max(100, "Payment Percentage must be between 0 and 100%").required("Pay Percentage is required"),
    pay_day: yup.string().required("Pay Day is required"),
  });


  const handleEditLandlord = async () => {
      try {
        await editLandlordSchema.validate(selectedLandlord, { abortEarly: false });
      } catch (error) {
        const newError = {};
        error.inner.forEach((err) => {
          newError[err.path] = err.message;
        });
        setValidationErrors(newError);
        return;
      }

    setIsSubmittingData(true);
    const editPayload = {
      user_details: {
        phone_number: selectedLandlord.phone_number,
        first_name: selectedLandlord.first_name,
        last_name: selectedLandlord.last_name,
        username: selectedLandlord.username,
        email: selectedLandlord.email,
      },
      business_name: selectedLandlord.business_name,
      pay_day: selectedLandlord.pay_day,
      pay_percentage: selectedLandlord.pay_percentage,
    };
    if (typeof selectedLandlord.is_anonymous === "boolean") {
      editPayload.is_anonymous = selectedLandlord.is_anonymous;
    } else if (selectedLandlord.is_anonymous === 1 || selectedLandlord.is_anonymous === 0) {
      editPayload.is_anonymous = selectedLandlord.is_anonymous === 1;
    }
    await axios
      .post(`${baseURL}/landlords/admin/landlords/details/${selectedLandlord.id}`, editPayload)
      .then((response) => {setIsSubmittingData(false);})
      .catch((error) => {setIsSubmittingData(false);});
    handleClose();
    setNewItemData(true);
    setSelectedLandlord({});
  };

  const handleAddLandlordSubmit = async () => {
    try {
      await landlordSchema.validate(newLandlord, { abortEarly: false });
    } catch (error) {
      const newError = {};
      error.inner.forEach((err) => {
        newError[err.path] = err.message;
      });
      setValidationErrors(newError);
      return;
    }

    setIsSubmittingData(true);
    try {
      const payload = buildCreateLandlordPayload(newLandlord);
      const response = await axios.post(`${baseURL}/admin/landlords/create`, payload);
      const data = response.data?.data ?? response.data;
      setNewItemData(true);
      setNewLandlord({ ...emptyNewLandlord });
      setValidationErrors({});

      let successText = data?.message || "Landlord added successfully.";
      if (data?.password_generated && data?.temporary_password) {
        successText = `${successText} Temporary password: ${data.temporary_password}`;
      }
      setOpenSnack({ open: true, message: successText, severity: "success" });
      handleDialogClose();
    } catch (error) {
      const apiErrors = error.response?.data;
      if (apiErrors && typeof apiErrors === "object" && !apiErrors.message) {
        const fieldErrors = {};
        Object.entries(apiErrors).forEach(([key, value]) => {
          const field = key === "username" || key === "phone_number" ? "contact" : key;
          fieldErrors[field] = Array.isArray(value) ? value.join(" ") : String(value);
        });
        setValidationErrors(fieldErrors);
      }
      setOpenSnack({
        open: true,
        message: error.response?.data?.message || "Failed to create landlord",
        severity: "error",
      });
    } finally {
      setIsSubmittingData(false);
    }
  };

  

  const handleFilterTypeChange = (event) => setFilterType(event.target.value);

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setSelectedLandlord((prev) => ({ ...prev, [name]: value }));
    if (!value.trim()) {
      setValidationErrors((prev) => ({ ...prev, [name]: `${name} is required`}));
    } else {
      setValidationErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  const handleChange = async (event) => {
    const { name, value } = event.target;
    setNewLandlord((prev) => ({ ...prev, [name]: value }));
    if (name === "contact") {
      try {
        await normalFieldsSchema.validateAt("contact", { contact: value });
        setValidationErrors((prev) => ({ ...prev, contact: "" }));
      } catch (error) {
        const message = error.inner?.[0]?.message || error.message;
        setValidationErrors((prev) => ({ ...prev, contact: message }));
      }
    } else if (!value.trim()) {
      setValidationErrors((prev) => ({ ...prev, [name]: `${name} is required` }));
    } else {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  if (successMessage) {
    setTimeout(() => {
      setSuccessMessage(""); 
    }, 2000);
  }
  return (
    <Box m="10px">
      <Box container spacing={2}>
        <Box item xs={12}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{ fontWeight: "bold", mb: 5, fontSize: 25 }}
          >
            Landlords
          </Typography>

          {successMessage && (
            <p sx={{ color: "green", fontSize: "14px" }}>{successMessage}</p>
          )}

          <Grid container spacing={2} sx={{ mb: 2 }} alignItems="center">
            <Grid item xs={12} md={4}>
              <FormControl variant="outlined" size="small" sx={{ flexGrow: 1, margin: "5px" }}>
                <InputLabel>Filter By</InputLabel>
                <Select
                  value={filterType}
                  onChange={handleFilterTypeChange}
                  label="Filter By"
                  sx={{ width: 300, fontSize: 14 }}
                >
                  <MenuItem value="landlordNumber">Landlord Number</MenuItem>
                  <MenuItem value="landlordName">Landlord Name</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <Typography component="span" variant="body2" sx={{ mr: 1, fontWeight: 500 }}>Status:</Typography>
              <ToggleButtonGroup
                value={statusFilter}
                exclusive
                onChange={(_, value) => value != null && setStatusFilter(value)}
                size="small"
                sx={{ mt: 0.5, flexWrap: "wrap" }}
              >
                <ToggleButton value="all">All</ToggleButton>
                <ToggleButton value="active">Active</ToggleButton>
                <ToggleButton value="inactivated">Inactivated</ToggleButton>
                <ToggleButton value="anonymous">Anonymous</ToggleButton>
              </ToggleButtonGroup>
            </Grid>
            <Grid item xs={12} md={4} sx={{ margin: "25px 20px 5px" }}>
              <Button variant="contained" color="primary" onClick={() => setOpenDialog(true)}>
                Add Landlord
              </Button>
            </Grid>
          </Grid>
          <LandlordsTable
            data={landlords}
            handleMenuClick={handleMenuClick}
            handleClickPopOver={handleClickPopOver}
            loading={isLoadingLandlords}
            error={error}
            setSelectedLandlord={setSelectedLandlord}
          />
        </Box>
      </Box>

      <TopSnack open={openSnack.open} severity={openSnack.severity} message={openSnack.message} handleCloseSnack={handleCloseSnack} />

      <LandlordsPopover
        popperId={popperId}
        openPopper={openPopper}
        anchorEl={anchorEl}
        handleClosePopOver={handleClosePopOver}
        handleClickOpen={handleClickOpen}
        navigate={navigate}
        selectedLandlord={selectedLandlord}
        setSelectedLandlord={setSelectedLandlord}
        handleViewLandlord={handleViewLandlord}
        handleModalView={handleModalView}
        isInactive={!!selectedLandlord?.date_inactivated}
        onToggleEinvoicing={handleToggleEinvoicing}
        isTogglingEinvoicing={isTogglingEinvoicing}
        onOpenEfrisOnboarding={handleOpenEfrisOnboarding}
      />

      <EfrisOnboardingDialog
        open={Boolean(efrisLandlord)}
        landlord={efrisLandlord}
        onClose={() => setEfrisLandlord(null)}
        onChanged={() => setNewItemData(true)}
      />

      <FormDialog
        open={openDialog}
        title="Add Landlord"
        text="Account, payout schedule, and payout destination. A temporary password is generated by the server."
        width="md"
        content={
          <AddLandlordForm
            newLandlord={newLandlord}
            handleChange={handleChange}
            validationErrors={validationErrors}
          />
        }
        errorMessage={errorMessage}
        setErrorMessage={setErrorMessage}
        handleClose={handleDialogClose}
        handleSubmit={handleAddLandlordSubmit}
        isSubmittingData={isSubmittingData}
      />

      {modal === "delete" ? (
        <FormDialog
          title="Delete Property"
          enableActions={true}
          open={open}
          content="Are you sure you want to delete this Landlord?"
          handleClose={handleClose}
          handleSubmit={handleDelete}
          buttonText="Delete"
        />
      ) : modal === "inactivate" ? (
        <FormDialog
          title="Inactivate Landlord"
          enableActions={true}
          open={open}
          text="The landlord will be marked as inactive but all data will be preserved. Select the date of inactivation."
          content={
            <TextField
              margin="dense"
              label="Date inactivated"
              type="date"
              value={inactivateDate}
              onChange={(e) => setInactivateDate(e.target.value)}
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().slice(0, 10) }}
            />
          }
          handleClose={handleClose}
          handleSubmit={handleInactivate}
          buttonText="Inactivate"
          isSubmittingData={isSubmittingData}
        />
      ) : modal === "reactivate" ? (
        <FormDialog
          title="Reactivate Landlord"
          enableActions={true}
          open={open}
          content="Are you sure you want to reactivate this landlord?"
          handleClose={handleClose}
          handleSubmit={handleReactivate}
          buttonText="Reactivate"
          isSubmittingData={isSubmittingData}
        />
      ) : modal === "edit" ? (
        <FormDialog
          open={open}
          title="Edit Landlord"
          text="Edit landlord Details"
          content={
            <EditLandlordForm
              newLandlord={selectedLandlord}
              handleChange={handleEditChange}
              validationErrors={validationErrors}
            />
          }
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          handleClose={handleClose}
          handleSubmit={handleEditLandlord}
          isSubmittingData={isSubmittingData}
        />
      ) : modal === "message" ? (
        <FormDialog
          open={open}
          title="Send Message"
          content={
            <TextField
              autoFocus
              margin="dense"
              label="Message"
              type="text"
              fullWidth
              multiline
              rows={4}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          }
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          handleClose={handleClose}
          handleSubmit={handleSendMessage}
          isSubmittingData={isSubmittingData}
        />
      ) : null}
    </Box>
  );
};

export default Landlords;
